import React, { useEffect, useState } from 'react';
import { categoryApi, uploadApi } from '../services/api';
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight, Upload } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY_CAT = { name: '', description: '', imageUrl: '', slug: '' };
const EMPTY_SUB = { name: '', description: '', imageUrl: '', slug: '' };

export default function AdminCategories() {
  const [categories,    setCategories]    = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [expanded,      setExpanded]      = useState({});
  const [loading,       setLoading]       = useState(true);

  // Modal state
  const [catModal,  setCatModal]  = useState(null);  // null | { mode:'add'|'edit', data }
  const [subModal,  setSubModal]  = useState(null);  // null | { mode, data, categoryId }
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    const [catRes, subRes] = await Promise.all([categoryApi.getAll(), categoryApi.getAllSubs()]);
    setCategories(catRes.data);
    setSubcategories(subRes.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const handleImageUpload = async (e, setState, field = 'imageUrl') => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadApi.upload(file, 'categories');
      setState((p) => ({ ...p, [field]: res.data.url }));
    } catch {
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ─── Category CRUD ─────────────────────────────────────────
  const saveCategory = async () => {
    const { mode, data } = catModal;
    try {
      if (mode === 'add') {
        await categoryApi.create(data);
        toast.success('Category created');
      } else {
        await categoryApi.update(data.id, data);
        toast.success('Category updated');
      }
      setCatModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error saving category');
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    await categoryApi.delete(id);
    toast.success('Category deleted');
    load();
  };

  // ─── Subcategory CRUD ───────────────────────────────────────
  const saveSubcategory = async () => {
    const { mode, data, categoryId } = subModal;
    try {
      if (mode === 'add') {
        await categoryApi.createSub(categoryId, data);
        toast.success('Subcategory created');
      } else {
        await categoryApi.updateSub(data.id, data);
        toast.success('Subcategory updated');
      }
      setSubModal(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error saving subcategory');
    }
  };

  const deleteSub = async (id) => {
    if (!window.confirm('Delete this subcategory?')) return;
    await categoryApi.deleteSub(id);
    toast.success('Subcategory deleted');
    load();
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1 className="admin-page-title">Categories</h1>
        <button className="btn btn-primary" onClick={() => setCatModal({ mode: 'add', data: { ...EMPTY_CAT } })}>
          <Plus size={18} /> Add Category
        </button>
      </div>

      {loading ? <div className="spinner" /> : (
        <div className="categories-list">
          {categories.map((cat) => {
            const subs = subcategories.filter((s) => s.categoryId === cat.id);
            return (
              <div key={cat.id} className="category-block">
                <div className="category-row">
                  <button className="expand-btn" onClick={() => toggleExpand(cat.id)}>
                    {expanded[cat.id] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  {cat.imageUrl && <img src={cat.imageUrl} alt={cat.name} className="row-thumbnail" />}
                  <div className="row-info">
                    <span className="row-name">{cat.name}</span>
                    <span className="row-meta">{subs.length} subcategories</span>
                  </div>
                  <div className="row-actions">
                    <button className="icon-btn" onClick={() => setCatModal({ mode: 'edit', data: { ...cat } })}>
                      <Pencil size={16} />
                    </button>
                    <button className="icon-btn danger" onClick={() => deleteCategory(cat.id)}>
                      <Trash2 size={16} />
                    </button>
                    <button className="btn btn-sm btn-outline" onClick={() =>
                      setSubModal({ mode: 'add', data: { ...EMPTY_SUB }, categoryId: cat.id })
                    }>
                      <Plus size={14} /> Sub
                    </button>
                  </div>
                </div>

                {expanded[cat.id] && (
                  <div className="subcategory-list">
                    {subs.length === 0 && <p className="empty-sub">No subcategories yet.</p>}
                    {subs.map((sub) => (
                      <div key={sub.id} className="subcategory-row">
                        {sub.imageUrl && <img src={sub.imageUrl} alt={sub.name} className="row-thumbnail sm" />}
                        <span className="row-name">{sub.name}</span>
                        <div className="row-actions">
                          <button className="icon-btn" onClick={() => setSubModal({ mode: 'edit', data: { ...sub }, categoryId: cat.id })}>
                            <Pencil size={14} />
                          </button>
                          <button className="icon-btn danger" onClick={() => deleteSub(sub.id)}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Category Modal */}
      {catModal && (
        <div className="modal-overlay" onClick={() => setCatModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{catModal.mode === 'add' ? 'Add Category' : 'Edit Category'}</h2>
            <div className="form-field">
              <label>Name *</label>
              <input value={catModal.data.name}
                onChange={(e) => setCatModal({ ...catModal, data: { ...catModal.data, name: e.target.value } })}
                placeholder="e.g. Premixes" />
            </div>
            <div className="form-field">
              <label>Description</label>
              <input value={catModal.data.description}
                onChange={(e) => setCatModal({ ...catModal, data: { ...catModal.data, description: e.target.value } })}
                placeholder="Short description" />
            </div>
            <div className="form-field">
              <label>Image</label>
              <div className="image-upload-row">
                {catModal.data.imageUrl && <img src={catModal.data.imageUrl} alt="preview" className="upload-preview" />}
                <label className="upload-label">
                  <Upload size={18}/> {uploading ? 'Uploading…' : 'Upload Image'}
                  <input type="file" accept="image/*" hidden
                    onChange={(e) => handleImageUpload(e, (fn) => setCatModal((p) => ({ ...p, data: fn(p.data) })))}
                  />
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setCatModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveCategory} disabled={uploading}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Subcategory Modal */}
      {subModal && (
        <div className="modal-overlay" onClick={() => setSubModal(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{subModal.mode === 'add' ? 'Add Subcategory' : 'Edit Subcategory'}</h2>
            <div className="form-field">
              <label>Name *</label>
              <input value={subModal.data.name}
                onChange={(e) => setSubModal({ ...subModal, data: { ...subModal.data, name: e.target.value } })}
                placeholder="e.g. Ice-cream Premix" />
            </div>
            <div className="form-field">
              <label>Description</label>
              <input value={subModal.data.description}
                onChange={(e) => setSubModal({ ...subModal, data: { ...subModal.data, description: e.target.value } })}
                placeholder="Short description" />
            </div>
            <div className="form-field">
              <label>Image</label>
              <div className="image-upload-row">
                {subModal.data.imageUrl && <img src={subModal.data.imageUrl} alt="preview" className="upload-preview" />}
                <label className="upload-label">
                  <Upload size={18}/> {uploading ? 'Uploading…' : 'Upload Image'}
                  <input type="file" accept="image/*" hidden
                    onChange={(e) => handleImageUpload(e, (fn) => setSubModal((p) => ({ ...p, data: fn(p.data) })))}
                  />
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setSubModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveSubcategory} disabled={uploading}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
